import { defineStore } from 'pinia';
import axios from 'axios';
import { loginWithPasskey } from '@/lib/webauthn';
import { resolveGuestCartId } from '@/globalFunctions';
import { useCartStore } from '@/stores/cart';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    permissions: [],
    hydrated: false,
  }),
  actions: {
    async init() {
      if (window.Laravel?.loggedIn) {
        try {
          const [userRes, permissionsRes] = await Promise.all([
            axios.get(route('api.v2.user.me'), { withCredentials: true }),
            axios.get(route('api.v2.user.me.permissions'), { withCredentials: true })
          ]);
          this.user = userRes.data;
          this.permissions = permissionsRes.data;
        } catch (e) {
          this.user = null;
          this.permissions = [];
        }
      }
      this.hydrated = true;
    },

    setUser(user) {
      this.user = user;
    },
    setPermissions(permissions) {
      this.permissions = permissions;
    },
    async logout() {
      this.user = null;
      this.permissions = [];
      document.cookie = 'mt-2019-cart=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
      await useCartStore().discardPreservedCart();
    },
    async register(formData) {
      try {
        const cartId = resolveGuestCartId(useCartStore().cartId);
        const response = await axios.post(route('register'), { ...formData, cartId }, {
          headers: { 'Accept': 'application/json' }
        });

        this.user = response.data.user ?? null;
        this.permissions = response.data.permissions ?? [];

        return { success: true, data: response.data };
      } catch (error) {
        const data = error.response?.data || { message: error.message };
        const status = error.response?.status ?? null;
        return { success: false, error: { ...data, status } };
      }
    },
    async login({ email, password, remember }) {
      try {
        await axios.get('/sanctum/csrf-cookie', { withCredentials: true });

        const result = await axios.post(route('login'), { email, password, remember }, {
          headers: { Accept: 'application/json' },
          withCredentials: true,
        });

        await this.init();

        return { success: true, redirect_url: result.data.redirect_url };
      } catch (error) {
        const data = error.response?.data || { message: error.message };
        return { success: false, error: data };
      }
    },
    async loginWithPasskey() {
      try {
        await loginWithPasskey();

        // Mirror the password login: the session cookie is set, hydrate the user.
        if (window.Laravel) {
          window.Laravel.loggedIn = true;
        }
        await this.init();

        return { success: true };
      } catch (error) {
        const data = error.response?.data || { message: error.message };
        return { success: false, error: data };
      }
    }
  },
  getters: {
    isAuthenticated: (state) => !!state.user,
    hasPermission: (state) => (permission) => state.permissions.includes(permission),
    partnerId: (state) => state.user?.partner_id || null,
    isOnboardingPending: (state) => !!state.user && !state.user.onboarding_completed_at,
    hasSeenOnboardingPage: (state) => (pageId) => {
      const pages = state.user?.onboarding_pages_seen
      return Array.isArray(pages) ? pages.includes(pageId) : false
    },
  },
});
